import { FC, useEffect } from 'react';
import { apiPollContractStatus } from '../../../api/api';
import { ShopContent } from '../../../types/cms_shared_types';
import { Button } from '../../Common/Button';
import { Conditional } from '../../Common/Conditional';
import { LoadingSpinner } from '../../Common/LoadingSpinner';
import { CancelBottomBar } from '../BottomBar/CancelBottomBar';
import { Step } from '../Step';
import { trackEvent } from '../../../helper/analytics';
import { Steps } from '../../../types/webshop';

interface SigningPageProps {
  onCancelSigning: () => void;
  cmsShopContent: ShopContent;
  contractId?: number;
  contractAccessLink: string;
  setCurrentStep: (arg0: Steps) => void;
  setSigningErrorOccurred: (arg0: boolean) => void;
  analyticsCategoryIndicator: string;
}

export const SigningPage: FC<SigningPageProps> = ({
  onCancelSigning,
  cmsShopContent,
  contractId,
  contractAccessLink,
  setCurrentStep,
  setSigningErrorOccurred,
  analyticsCategoryIndicator,
}) => {
  const CONTRACT_STATUS_POLL_INTERVAL_IN_MSEC = 5000;
  const warnUser = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    if (contractId === undefined) {
      return;
    }

    const id = window.setInterval(async () => {
      const res = await apiPollContractStatus(contractId);
      const isSigned = res.data.signedStatus === 'signed';

      if (isSigned || res.data.signedStatus === 'error') {
        setSigningErrorOccurred(res.data.signedStatus === 'error');
        setCurrentStep(Steps.LANDING_PAGE);
      }

      if (isSigned) {
        trackEvent('SignedAgreement', analyticsCategoryIndicator);
      }
    }, CONTRACT_STATUS_POLL_INTERVAL_IN_MSEC);

    return () => {
      clearInterval(id);
    };
  }, [contractId, setCurrentStep, setSigningErrorOccurred, analyticsCategoryIndicator]);

  useEffect(() => {
    window.addEventListener('beforeunload', warnUser);
    return () => {
      window.removeEventListener('beforeunload', warnUser);
    };
  }, []);

  return (
    <Step
      title={cmsShopContent.signingPageStep.title}
      content={
        <div className="flex flex-col items-center max-w-3xl mx-auto mt-6 text-center sm:w-9/12 sm:mt-0">
          <div className="mb-12 text-lg font-extrabold text-text-dark sm:text-xl">
            {cmsShopContent.signingPageStep.subtitle}
          </div>

          <LoadingSpinner className="mb-4" />
          <Conditional
            condition={contractAccessLink !== ''}
            trueRender={
              <Button
                text={cmsShopContent.signingPageStep.redirectButton}
                onClick={() => window.open(contractAccessLink, '_blank')}
                filled
              />
            }
          />

          <div style={{ hyphens: 'auto' }} className="mt-8 text-base text-text-dark">
            {cmsShopContent.signingPageStep.description}
          </div>
        </div>
      }
      bottomBar={<CancelBottomBar onClick={onCancelSigning} cmsShopContent={cmsShopContent} />}
    />
  );
};
